/**
 * Qual modelo de checklist de EXECUÇÃO vale para cada plano preventivo.
 *
 * Precedência quando a O.S. é aberta (a mais específica vence):
 * grupo + componente → grupo → componente → padrão (qualquer grupo e componente).
 * Só aparecem modelos do tipo "Execução da O.S.", criados em "Novo modelo".
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { MaintenanceChecklistBindingDto, MaintenanceKnownComponentDto, MaintenanceVehicleGroupListDto } from "@bi/types";
import { api, ApiError } from "../../lib/api";

interface BindingsDto {
  bindings: MaintenanceChecklistBindingDto[];
  templates: Array<{ id: string; name: string; active: boolean }>;
}

export function ChecklistBindingsPanel({ editavel }: { editavel: boolean }) {
  const qc = useQueryClient();
  const [erro, setErro] = useState<string | null>(null);
  const [grupo, setGrupo] = useState("");
  const [componente, setComponente] = useState("");
  const [modelo, setModelo] = useState("");

  const dados = useQuery({ queryKey: ["maintenance", "checklist-bindings"], queryFn: () => api.get<BindingsDto>("/maintenance/checklist-bindings") });
  const grupos = useQuery({ queryKey: ["maintenance", "vehicle-groups"], queryFn: () => api.get<MaintenanceVehicleGroupListDto>("/maintenance/vehicle-groups") });
  const componentes = useQuery({
    queryKey: ["maintenance", "vehicle-groups", "components"],
    queryFn: () => api.get<{ items: MaintenanceKnownComponentDto[] }>("/maintenance/vehicle-groups/components"),
  });

  const ok = () => { setErro(null); qc.invalidateQueries({ queryKey: ["maintenance", "checklist-bindings"] }); };
  const falha = (e: unknown) => setErro(e instanceof ApiError ? e.message : "Não foi possível salvar.");
  const salvar = useMutation({
    mutationFn: () => api.put("/maintenance/checklist-bindings", { vehicleGroupId: grupo || null, componentCode: componente || null, templateId: modelo }),
    onSuccess: () => { setModelo(""); ok(); }, onError: falha,
  });
  const remover = useMutation({ mutationFn: (id: string) => api.del(`/maintenance/checklist-bindings/${id}`), onSuccess: ok, onError: falha });

  const nomeComponente = (code: string | null) => (code ? componentes.data?.items.find((c) => c.code === code)?.label ?? code : "Qualquer componente");
  const modelos = (dados.data?.templates ?? []).filter((t) => t.active);

  return (
    <section className="card p-5 space-y-3">
      <div>
        <h3 className="font-semibold text-gray-900">Checklist da O.S. preventiva</h3>
        <p className="text-sm text-gray-500">
          Ao abrir uma O.S. pelo plano, o sistema carrega o modelo vinculado ao grupo da placa e ao componente. O vínculo mais específico vence.
        </p>
      </div>
      {erro && <p className="rounded-lg border border-danger-600/30 bg-danger-50 p-2 text-sm text-danger-600">{erro}</p>}
      <ul className="divide-y divide-gray-100">
        {(dados.data?.bindings ?? []).map((b) => (
          <li key={b.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
            <span>
              <span className="font-medium text-gray-900">{b.templateName ?? "Modelo removido"}</span>
              <span className="block text-xs text-gray-500">{b.groupName ?? "Qualquer grupo"} · {nomeComponente(b.componentCode)}</span>
            </span>
            {editavel && <button className="btn-ghost text-xs text-danger-600" disabled={remover.isPending} onClick={() => remover.mutate(b.id)}>Remover</button>}
          </li>
        ))}
        {dados.data?.bindings.length === 0 && <li className="py-2 text-sm text-gray-500">Nenhum vínculo. As O.S. preventivas abrem sem checklist.</li>}
      </ul>
      {editavel && (
        <div className="grid gap-2 sm:grid-cols-4">
          <select className="input" value={grupo} onChange={(e) => setGrupo(e.target.value)} aria-label="Grupo">
            <option value="">Qualquer grupo</option>
            {(grupos.data?.groups ?? []).filter((g) => g.active).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
          <select className="input" value={componente} onChange={(e) => setComponente(e.target.value)} aria-label="Componente">
            <option value="">Qualquer componente</option>
            {(componentes.data?.items ?? []).map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
          <select className="input" value={modelo} onChange={(e) => setModelo(e.target.value)} aria-label="Modelo de checklist">
            <option value="">Modelo de execução…</option>
            {modelos.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <button className="btn-secondary" disabled={!modelo || salvar.isPending} onClick={() => salvar.mutate()}>Vincular</button>
        </div>
      )}
      {editavel && modelos.length === 0 && (
        <p className="text-xs text-gray-500">Ainda não há modelo do tipo “Execução da O.S.”. Crie um em “Novo modelo”.</p>
      )}
    </section>
  );
}
